import type { FooterProps } from './Footer.types';

import React from 'react';
import { useSelector } from 'react-redux';
import * as selectors from 'features/sessions/selectors';
import { FooterButton } from '../Buttons';
import { SessionSectionType } from '../../Session.constants';
import './_footer.scss';

export const Footer = ({
  sectionType,
  reviewPhase,
  reviewHasPrev,
  isSessionCompleted,
  isHotkeyDisabled,
  onReviewScreen,
  onReviewSubset,
  onNavigatePrev,
  onNavigateNext,
  onReturnToOverview,
  onEndExamModalToggle,
  onReportQuestionToggle,
  onEndSectionModalToggle,
  onNavigatorModalToggle,
  onEndReview,
}: FooterProps) => {
  const currentSection = useSelector(selectors.selectCurrentSection);
  const prevQuestionId = useSelector(selectors.selectPrevQuestionId);
  const nextQuestionId = useSelector(selectors.selectNextQuestionId);
  const isQuestionResponseLoading = useSelector(selectors.selectIsQuestionResponseLoading);

  // During subset review the section type is QUESTION (the interactive
  // question UI is reused), so the normal question chrome is suppressed and
  // replaced with the "Review Screen" return control.
  const isReviewing = reviewPhase === 'reviewing';

  if (sectionType === SessionSectionType.OVERVIEW) return null;

  return (
    <>
      <div className="footer__container">
        <div className="footer__buttons">
          {sectionType === SessionSectionType.PACKAGE_INSTRUCTION && (
            <div className="footer__button-right">
              <FooterButton type="end_exam" onClick={onEndExamModalToggle(true)} isHotkeyDisabled={isHotkeyDisabled} />
            </div>
          )}
          {sectionType === SessionSectionType.QUESTION && !isReviewing && (
            <div className="footer__button-right">
              {isSessionCompleted ? (
                <FooterButton type="return_overview" onClick={onReturnToOverview} isHotkeyDisabled={isHotkeyDisabled} />
              ) : (
                <FooterButton
                  type="end_section"
                  onClick={onEndSectionModalToggle(true)}
                  isHotkeyDisabled={isHotkeyDisabled}
                />
              )}
            </div>
          )}
          {sectionType === SessionSectionType.REVIEW_PRESCORE && (
            <div className="footer__button-right">
              <FooterButton type="end_review" onClick={onEndReview} isHotkeyDisabled={isHotkeyDisabled} />
            </div>
          )}
        </div>

        <div className="footer__buttons">
        {sectionType === SessionSectionType.QUESTION && currentSection && !isSessionCompleted && !isReviewing && (
          <div className="">
            <FooterButton
              type="report"
              onClick={onReportQuestionToggle(true)}
              isDisabled={isQuestionResponseLoading}
              isHotkeyDisabled={isHotkeyDisabled}
            />
          </div>
           )}
          {sectionType === SessionSectionType.QUESTION &&
            (isReviewing ? reviewHasPrev : Boolean(prevQuestionId)) && (
              <div className="footer__button-left">
                <FooterButton
                  type="prev"
                  onClick={onNavigatePrev}
                  isDisabled={isQuestionResponseLoading}
                  isHotkeyDisabled={isHotkeyDisabled}
                />
              </div>
            )}
          {sectionType === SessionSectionType.QUESTION && currentSection && !isSessionCompleted && !isReviewing && (
            <div className="footer__button-left">
              <FooterButton
                type="navigator"
                onClick={onNavigatorModalToggle(true)}
                isDisabled={isQuestionResponseLoading}
                isHotkeyDisabled={isHotkeyDisabled}
              />
            </div>
          )}
          {isReviewing && (
            <div className="footer__button-left">
              <FooterButton
                type="review_screen"
                onClick={onReviewScreen}
                isDisabled={isQuestionResponseLoading}
                isHotkeyDisabled={isHotkeyDisabled}
              />
            </div>
          )}
          {sectionType !== SessionSectionType.REVIEW_PRESCORE &&
            (sectionType === SessionSectionType.PACKAGE_INSTRUCTION ||
              nextQuestionId ||
              (sectionType === SessionSectionType.QUESTION && !!currentSection && !isSessionCompleted)) && (
            <div className="footer__button-left">
              <FooterButton
                type="next"
                onClick={onNavigateNext}
                isDisabled={isQuestionResponseLoading}
                isHotkeyDisabled={isHotkeyDisabled}
              />
            </div>
          )}
          {sectionType === SessionSectionType.REVIEW_PRESCORE && (
            <>
              <div className="footer__button-left">
                <FooterButton
                  type="review_all"
                  onClick={() => onReviewSubset('all')}
                  isHotkeyDisabled={isHotkeyDisabled}
                />
              </div>
              <div className="footer__button-left">
                <FooterButton
                  type="review_incomplete"
                  onClick={() => onReviewSubset('incomplete')}
                  isHotkeyDisabled={isHotkeyDisabled}
                />
              </div>
              <div className="footer__button-left">
                <FooterButton
                  type="review_flagged"
                  onClick={() => onReviewSubset('flagged')}
                  isHotkeyDisabled={isHotkeyDisabled}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
